import React from 'react'
import Antecedence from '../../taskcomponents/Antecedence.js'
import SideConditionRule from '../../taskcomponents/SideConditionRule.js'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faEraser} from '@fortawesome/free-solid-svg-icons'
import {requestLogRestService} from "../../taskcomponents/Task";
import Consequence from "../../taskcomponents/Consequence";
import {USER_CLEAR} from "../../constants";

/**
 * Displays the corresponding deduction rule where items and production rules
 * can be dropped.
 */
class CfgCykCompleteUnary extends React.Component {
    initial_values = {
        antecedence: '',
        nt: '',
        displayNt: 'B',
        i: '',
        displayI: 'i',
        j: '',
        displayJ: 'j',
        lhs: '',
        displayLhs: 'A',
        rhs: '',
        displayRhs: 'B',
        prodRule: '',
        canDropItem: 0,
        canDropRule: 0,
        displayItem: '',
        displayRule: '',
        eraseDisabled: true
    };

    constructor(props) {
        super(props);
        this.checkNewItem = this.checkNewItem.bind(this);
        this.clear = this.clear.bind(this);
        this.extractAdditionalItemValues = this.extractAdditionalItemValues.bind(this);
        this.extractAdditionalRuleValues = this.extractAdditionalRuleValues.bind(this);
        this.setDisplayItem = this.setDisplayItem.bind(this);
        this.state = this.initial_values;
    }

    /**
     * Set item as antecedence in state and extract different other values from
     * it to set them in state.
     */
    setAntecedence(item) {
        this.setState({
            antecedence: item,
            eraseDisabled: false
        });
        this.extractAdditionalItemValues(item);
        this.checkNewItem();
    }

    extractAdditionalItemValues(item) {
        const comma1 = item.itemForm.indexOf(',');
        const comma2 = item.itemForm.lastIndexOf(',');
        const nt = item.itemForm.substring(1, comma1);
        const i = item.itemForm.substring(comma1 + 1, comma2);
        const j = item.itemForm.substring(comma2 + 1, item.itemForm.length - 1);
        this.setState({
            nt: nt,
            i: i,
            j: j,
            canDropItem: 0,
            canDropRule: 0,
            displayItem: ''
        });
    }

    extractAdditionalDisplayItemValues(item) {
        const comma1 = item.itemForm.indexOf(',');
        const comma2 = item.itemForm.lastIndexOf(',');
        const nt = item.itemForm.substring(1, comma1);
        const i = item.itemForm.substring(comma1 + 1, comma2);
        const j = item.itemForm.substring(comma2 + 1, item.itemForm.length - 1);
        this.setState({
            displayNt: nt,
            displayI: i,
            displayJ: j,
            canDropItem: 0,
            canDropRule: 0,
            displayItem: ''
        });
    }

    /**
     * Set rule as production rule in state and extract different other values
     * from it to be set in state.
     */
    setProdRule(rule) {
        this.setState({prodRule: rule, eraseDisabled: false});
        this.extractAdditionalRuleValues(rule);
        this.checkNewItem();
    }

    extractAdditionalRuleValues(rule) {
        const ruleSplit = rule.rule.split('->');
        const lhs = ruleSplit[0].trim();
        const rhs = ruleSplit[1].trim();
        this.setState({lhs: lhs, rhs: rhs, nt: rhs});
    }

    extractAdditionalDisplayRuleValues(rule) {
        const ruleSplit = rule.rule.split('->');
        const lhs = ruleSplit[0].trim();
        const rhs = ruleSplit[1].trim();
        this.setState({displayLhs: lhs, displayRhs: rhs, displayNt: rhs});
    }

    setDisplayRule(rule) {
        this.setState({displayRule: rule});
        if (rule === '') {
            if (this.state.prodRule === '') {
                if (this.state.antecedence === '') {
                    this.setState({displayLhs: 'A', displayRhs: 'B', displayNt: 'B'});
                } else {
                    this.setState({displayLhs: 'A'});
                    this.extractAdditionalDisplayItemValues(this.state.antecedence);
                }
            } else {
                this.extractAdditionalDisplayRuleValues(this.state.prodRule);
            }
        } else {
            this.extractAdditionalDisplayRuleValues(rule);
        }
    }

    /**
     * Returns true if item can be dropped on the antecedence component.
     */
    canDropItem(item) {
        const comma1 = item.itemForm.indexOf(',');
        const nt = item.itemForm.substring(1, comma1);
        if (this.state.prodRule === '') {
            return this.props.isNonterminal(nt);
        }
        return nt === this.state.rhs;
    }

    /**
     * Returns true if rule can be dropped on the side condition component.
     * Rule must be unary (single nonterminal on RHS).
     */
    canDropRule(rule) {
        const ruleSplit = rule.rule.split('->');
        const rhs = ruleSplit[1].trim();
        const rhsSplit = rhs.split(' ');
        // Must be a unary rule (single nonterminal on RHS)
        if (rhsSplit.length !== 1 || rhs === '' || rhs === 'ε') {
            return false;
        }
        if (this.state.antecedence === '') {
            return true;
        }
        return rhs === this.state.nt;
    }

    /**
     * Sets item as displayItem in state and extracts different values from it
     * to be set in state.
     */
    setDisplayItem(item) {
        this.setState({
            displayItem: item
        });
        if (item === '') {
            if (this.state.antecedence === '') {
                if (this.state.prodRule === '') {
                    this.setState({
                        displayNt: 'B',
                        displayI: 'i',
                        displayJ: 'j',
                        displayLhs: 'A',
                        displayRhs: 'B'
                    });
                } else {
                    this.setState({
                        displayI: 'i',
                        displayJ: 'j'
                    });
                    this.extractAdditionalDisplayRuleValues(this.state.prodRule);
                }
            } else {
                this.extractAdditionalDisplayItemValues(this.state.antecedence);
            }
        } else {
            this.extractAdditionalDisplayItemValues(item);
        }
    }

    /**
     * Sets the canDropItem value in the state. 0 - no coloring, 1 - positive
     * highlighting of some parts, -1 negative highlighting.
     */
    setCanDropItem(value) {
        this.setState({
            canDropItem: value
        });
    }

    /**
     * Sets the canDropRule value in the state. 0 - no coloring, 1 - positive
     * highlighting of some parts, -1 negative highlighting.
     */
    setCanDropRule(value) {
        this.setState({
            canDropRule: value
        });
    }

    /**
     * If both antecedence and production rule are specified a new item is
     * generated from them and send to the task to be added to the table.
     */
    checkNewItem() {
        if (this.state.antecedence !== '' && this.state.prodRule !== '') {
            let newItem = '[' + this.state.lhs + ',' + this.state.i + ','
                + this.state.j + ']';
            if (this.props.sendNewItemToTask(newItem, 'complete-unary '
                + this.state.prodRule.rule, [this.state.antecedence.id])) {
                this.clear();
            }
        }
    }

    /**
     * Resets the initial values and sends log request.
     */
    clearWithLog() {
        requestLogRestService(USER_CLEAR(this.props.userSession,
            'cfg-cyk-complete-unary'));
        this.clear();
    }

    /**
     * Resets the initial values of the state.
     */
    clear() {
        this.setState(this.initial_values);
    }

    render() {
        const nt = this.state.displayNt;
        const i = this.state.displayI;
        const j = this.state.displayJ;
        const lhs = this.state.displayLhs;
        const rhs = this.state.displayRhs;
        const canDropItem = this.state.canDropItem;
        const canDropRule = this.state.canDropRule;
        let canDropObject = 0;
        canDropObject = canDropItem !== 0 ? canDropItem : canDropObject;
        canDropObject = canDropRule !== 0 ? canDropRule : canDropObject;
        const itemHovering = canDropItem === 0 ? 0 : 1;
        const ruleHovering = this.state.displayRule === '' ? 0 : 1;
        return <div className='deduction-rule'>
            <div className='rule-name'>
                <button id='ded-rule-erase' disabled={this.state.eraseDisabled}
                        onClick={this.clearWithLog.bind(this)}>
                    <FontAwesomeIcon className={"clickable-icon"}
                                     icon={faEraser}/>
                    <span
                        className={'accessibility-label'}>clear <abbr>cyk</abbr> complete unary</span>
                </button>
                Complete Unary:
            </div>
            <div className='middle-rule-part'>
                <div
                    className='antecedence'>
                    <Antecedence antecedenceName='cfg-cyk-complete-unary'
                                 elementLists={[[nt], [i], [j]]}
                                 markings={[[canDropObject], [itemHovering], [itemHovering]]}
                                 setAntecedence={this.setAntecedence.bind(this)}
                                 canDropItem={this.canDropItem.bind(this)}
                                 setDisplayItem={this.setDisplayItem.bind(this)}
                                 setCanDropItem={this.setCanDropItem.bind(this)}
                                 userSession={this.props.userSession}
                                 maxItem={this.props.maxItem}/>
                </div>
                <hr/>
                <div
                    className='consequence'>
                    <Consequence
                        elementLists={[[lhs], [i], [j]]}
                        markings={[[ruleHovering], [itemHovering], [itemHovering]]}
                        maxItem={this.props.maxItem}/>
                </div>
            </div>
            <div className='side-condition'>
                <SideConditionRule
                    displayLhs={lhs} displayRhs={rhs}
                    setProdRule={this.setProdRule.bind(this)}
                    setDisplayRule={this.setDisplayRule.bind(this)}
                    canDropRule={this.canDropRule.bind(this)}
                    setCanDropRule={this.setCanDropRule.bind(this)}
                    canDropLhs={ruleHovering}
                    canDropRhs={canDropObject}
                    userSession={this.props.userSession}
                    ruleName='cfg-cyk-complete-unary'/> <span
                className={'unbreakable'}>∈ P</span>
            </div>
        </div>
    }
}

export {CfgCykCompleteUnary};
